import React, { useEffect, useState } from "react";
import { Button } from "components/Button";
import { Card } from "components/Card";
import { CheckBox } from "components/CheckBox";
import { InputFormField } from "components/InputFormField";
import { TextFormField } from "components/TextFormField";
import { useHistory } from "react-router-dom";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import { useAlert } from "hooks/useAlert";
import { iconWhiteTrash } from "assets/icons";
import { useFaqForm } from "./useFaqForm";
import { Container, FormSection, Wrapper, FormActions } from './styles';

interface FaqFormProps {
    eventId?: string;
    faqId?: string;
}

export const FaqForm: React.FC<FaqFormProps> = ({
    eventId,
    faqId
}) => {
    const { push } = useHistory();
    const { connectGraphQLApi } = useGraphQLAuth();
    const { setToast } = useToast();
    const { setTextAlertInterface, openAlert, closeAlert, alert } = useAlert();
    const [editingFaq, setEditingFaq] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    const onSuccess = () => {
        push(`/events/${eventId}`);
    };

    const {
        handleInputChange,
        handleCheckboxChange,
        inputsValues,
        touched,
        errors,
        handleSubmit,
        isLoading,
        isEditing,
    } = useFaqForm(onSuccess, editingFaq, eventId);

    const getErrorMessage = (error: any): string | undefined => {
        if (typeof error === 'string') return error;
        if (Array.isArray(error)) return error[0];
        return undefined;
    };

    // Load FAQ data for editing
    useEffect(() => {
        if (faqId) {
            loadFaqData();
        }
    }, [faqId]);

    // Handle alert confirmation for delete
    useEffect(() => {
        if (alert.typeContinue === "save") {
            executeDeleteFaq();
        }
    }, [alert]);

    const loadFaqData = async () => {
        if (!faqId) return;

        setLoading(true);

        try {
            const query = `
                query BackofficeFaq($eventFaqId: ID!) {
                    backofficeFaq(eventFaqId: $eventFaqId) {
                        eventFaqId
                        eventId
                        question
                        answer
                        displayOrder
                        isActive
                        insertDate
                        updateDate
                    }
                }
            `;

            const response = await connectGraphQLApi({
                query,
                variables: { eventFaqId: faqId },
                errorMessage: 'Falha ao carregar detalhes da FAQ',
                useNewApi: true,
            });

            const faqDetails = response.data.backofficeFaq;

            if (faqDetails) {
                // Verify that this FAQ belongs to the current event
                if (faqDetails.eventId !== eventId) {
                    setToast({
                        type: 'error',
                        message: 'FAQ não pertence a este evento',
                        isActive: true,
                        timeToRemoveToast: 3000,
                    });
                    push(`/events/${eventId}`);
                    return;
                }

                setEditingFaq(faqDetails);
            } else {
                setToast({
                    type: 'error',
                    message: 'FAQ não encontrada',
                    isActive: true,
                    timeToRemoveToast: 3000,
                });
                push(`/events/${eventId}`);
            }
        } catch (error) {
            console.error('Error loading FAQ details:', error);
            setToast({
                type: 'error',
                message: 'Erro ao carregar FAQ',
                isActive: true,
                timeToRemoveToast: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteFaq = () => {
        if (!faqId || !editingFaq) return;

        setTextAlertInterface({
            title: "Excluir FAQ?",
            description: `Ao continuar, a FAQ "${editingFaq.question}" e todas as suas informações serão removidas.\nAtenção! Essa ação não pode ser revertida.`,
            closeAlertText: "Cancelar",
            continueAlertText: "Excluir",
            closeAlertTypeBtn: "outline",
            continueAlertTypeBtn: "danger",
            positionCenter: true,
            actionNeeded: false,
        });
        
        openAlert(true);
    };

    const executeDeleteFaq = async () => {
        if (!editingFaq) return;

        try {
            const response = await connectGraphQLApi({
                query: `
                    mutation BackofficeDeleteEventFaq($eventFaqId: ID!) {
                        backofficeDeleteEventFaq(eventFaqId: $eventFaqId)
                    }
                `,
                variables: { eventFaqId: editingFaq.eventFaqId },
                errorMessage: 'Falha ao excluir FAQ',
                useNewApi: true,
            });

            if (response.data.backofficeDeleteEventFaq) {
                setToast({
                    type: 'success',
                    message: 'FAQ excluída com sucesso!',
                    isActive: true,
                    timeToRemoveToast: 3000,
                });

                // Redirect back to event details
                push(`/events/${eventId}`);
            } else {
                setToast({
                    type: 'error',
                    message: 'Falha ao excluir FAQ',
                    isActive: true,
                    timeToRemoveToast: 3000,
                });
            }
        } catch (error) {
            console.error('Error deleting FAQ:', error);
            setToast({
                type: 'error',
                message: 'Erro ao excluir FAQ. Tente novamente.',
                isActive: true,
                timeToRemoveToast: 5000,
            });
        } finally {
            closeAlert();
        }
    };

    if (loading) {
        return <Container>Carregando...</Container>;
    }

    return (
        <Container>
            <form onSubmit={handleSubmit}>
                <Card title="Informações da FAQ" typography="subtitle-medium">
                    <FormSection>
                        <Wrapper>
                            <InputFormField
                                label="Pergunta *"
                                name="question"
                                value={inputsValues.question}
                                onChange={handleInputChange}
                                error={touched.question && !!errors.question}
                                mensageError={getErrorMessage(errors.question)}
                                block
                            />
                        </Wrapper>

                        <Wrapper>
                            <TextFormField
                                label="Resposta *"
                                name="answer"
                                value={inputsValues.answer}
                                onChange={handleInputChange}
                                error={touched.answer && !!errors.answer}
                                mensageError={getErrorMessage(errors.answer)}
                                block
                                rows={4}
                            />
                        </Wrapper>

                        <Wrapper>
                            <InputFormField
                                label="Ordem de Exibição *"
                                name="displayOrder"
                                type="number"
                                value={inputsValues.displayOrder.toString()}
                                onChange={handleInputChange}
                                error={touched.displayOrder && !!errors.displayOrder}
                                mensageError={getErrorMessage(errors.displayOrder)}
                                block
                            />
                        </Wrapper>

                        <Wrapper>
                            <CheckBox
                                label="FAQ Ativa"
                                name="isActive"
                                value={inputsValues.isActive}
                                checked={inputsValues.isActive}
                                onChange={handleCheckboxChange}
                                checkedError={touched.isActive && !!errors.isActive}
                            />
                            {touched.isActive && errors.isActive && (
                                <span style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                    {getErrorMessage(errors.isActive)}
                                </span>
                            )}
                        </Wrapper>
                    </FormSection>
                </Card>

                <FormActions>
                    {isEditing ? (
                        <Button
                            label="Excluir FAQ"
                            typeBtn="danger"
                            icon={iconWhiteTrash}
                            onClick={handleDeleteFaq}
                            type="button"
                        />
                    ) : <div/>}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <Button
                            label="Cancelar"
                            typeBtn="outline"
                            onClick={() => push(`/events/${eventId}?tab=FAQs`)}
                            type="button"
                        />
                        <Button
                            label={isEditing ? "Salvar Alterações" : "Criar FAQ"}
                            typeBtn="blue"
                            type="submit"
                            load={isLoading}
                            disabled={isLoading}
                        />
                    </div>

                </FormActions>
            </form>
        </Container>
    );
}; 